import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Select, message, Button, Input, Table, Modal } from "antd";
import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API } from "../../constants";
import { Plus, Search, Table2, Trash2 } from "lucide-react";
import { AN_COLORS } from "../../design-system/tokens";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  SquareBadge,
  StatusPill,
  TableCard,
  anTableProps,
  fmtDate,
  tableEmpty,
} from "../../design-system/tableUi";
import DatabaseSettingsCard from "./DatabaseSettingsCard";

const DatabasesScreen = ({ showNotification, onDatabaseSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [dbNames, setDbNames] = useState([]); // from getUniqueDatabasenames
  const [dbList, setDbList] = useState([]); // from listDatabase
  const [totalDBCount, setTotalDBCount] = useState(0); // from listDatabase

  // Filters
  const [selectedDatabase, setSelectedDatabase] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  // Mirrors the Host input so typing stays responsive while the fetch is debounced.
  const [hostInput, setHostInput] = useState("");
  const [hostSearchTimeout, setHostSearchTimeout] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  // For user messages
  const [notification, contextHolder] = message.useMessage();

  const [isAgent, setIsAgent]= useState(false)

  useEffect(() => {
    if (!location.state) return;
    const { host } = location.state;
    if (host) {
      setIsAgent(true)
      setSelectedHost(host);
      setHostInput(host);
    }
    // clear navigation state after using it
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.state]);

  // Fetch unique database names on mount
  useEffect(() => {
    const fetchDbNames = async () => {
      try {
        const response = await axios.post(
          `${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`,
          {
            orgId: ORG_ID(),
            tenantId: DOMAIN_ID(),
          },
          {
            headers: {
              "X-Authorization": AUTH_TOKEN(),
              withCredentials: true,
            },
          },
        );

        setDbNames(response.data?.db_names || []);
      } catch (error) {
        console.error("Error fetching database names:", error?.response?.status, error?.message);
      }
    };

    fetchDbNames();
  }, []);

  // Handle host filter from VMScreen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hostFilter = params.get("host");
    if (hostFilter) {
      setSelectedHost(hostFilter);
      setHostInput(hostFilter);
    }
  }, []);

  // Actually fetch the DB list whenever filters/page change
  useEffect(() => {
    loadDatabaseData();
  }, [selectedDatabase, selectedStatus, selectedHost, currentPage, pageSize]);

  const loadDatabaseData = async () => {
    setIsLoading(true);
    const filters = [];
    if (selectedDatabase) {
      filters.push({
        filterParameter: "Database",
        filterValue: selectedDatabase,
      });
    }
    if (selectedStatus) {
      filters.push({ filterParameter: "Status", filterValue: selectedStatus });
    }
    if (selectedHost) {
      filters.push({ filterParameter:isAgent?"AgentHostIp": "Host", filterValue: selectedHost });
    }

    try {
      const { data, totalCount: tc } = await fetchDbList(
        filters,
        pageSize,
        currentPage + 1,
      );
      setDbList(data);
      setTotalCount(tc);
    } catch (error) {
      console.log("Failed");

      notification.error({ content: "Failed to fetch database list" });
    } finally {
      setIsLoading(false);
      setIsAgent(false)
    }
  };

  const fetchDbList = async (filters, limit, pageId) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listDatabase`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          filters,
          limit,
          page_id: pageId,
        },
        {
          headers: {
            "X-Authorization": AUTH_TOKEN(),
            withCredentials: true,
          },
        },
      );

      return {
        data: response.data?.data || [],
        totalCount: response.data?.total_count || 0,
      };
    } catch (error) {
      console.error("Error fetching database list:", error?.response?.status, error?.message);
      setDbList([]);
      setTotalDBCount(0);
    }
  };

  const deleteDatabase = (db) => {
    console.log("hostIP", db);
    Modal.confirm({
      title: `Are you sure you want to delete "${db.db_name}"?`,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const payLoad = {
            orgId: ORG_ID(),
            tenantId: DOMAIN_ID(),
            DatabaseHostIp: db.host,
            DatabaseName:db.db_name,
            DatabaseType:db.db_type

          };

          await axios.post(
            `${REACT_APP_API}/api/v1/databaseService/deleteDatabaseHost`,
            payLoad,
            {
              headers: {
                "Content-Type": "application/json",
              },
            },
          );
          notification.success("Database deleted successfully");
          loadDatabaseData();
        } catch (err) {
          console.error("Delete failed", err?.response?.status, err?.message);
        }
      },
    });
  };

  // -----------------------------------------------------------
  // 2. Fetch the full Database List (with pagination & filters)
  // -----------------------------------------------------------
  const handleMenuClick = async (db, action) => {
    switch (action) {
      case "delete":
        deleteDatabase(db);
        break;
      case "viewTables":
        navigate(`/pam/databases/dashboard`, {
          state: { host: db.host, database: db.db_name, activeTab: "tables" },
        });
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: "Database",
      dataIndex: "db_name",
      key: "db_name",
      render: (text) => (
        <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{text || "—"}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "db_type",
      key: "db_type",
      width: 130,
      render: (text) => <SquareBadge>{text}</SquareBadge>,
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (text) => <StatusPill status={text} />,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 190,
      render: (text) => <MonoCell>{fmtDate(text)}</MonoCell>,
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      render: (_, record) => (
        <RowActionMenu
          items={[
            {
              key: "viewTables",
              icon: <Table2 size={13} />,
              label: "View tables",
              onClick: () => handleMenuClick(record, "viewTables"),
            },
            { type: "divider" },
            {
              key: "delete",
              danger: true,
              icon: <Trash2 size={13} />,
              label: "Delete database",
              onClick: () => handleMenuClick(record, "delete"),
            },
          ]}
        />
      ),
    },
  ];

  const resetPage = () => setCurrentPage(0);

  const appliedFilters = [
    selectedDatabase && {
      key: "Database",
      label: `Database · ${selectedDatabase}`,
      clear: () => {
        setSelectedDatabase(null);
        resetPage();
      },
    },
    selectedStatus && {
      key: "Status",
      label: `Status · ${selectedStatus}`,
      clear: () => {
        setSelectedStatus(null);
        resetPage();
      },
    },
    selectedHost && {
      key: "Host",
      label: `Host · ${selectedHost}`,
      clear: () => {
        setSelectedHost(null);
        setHostInput("");
        resetPage();
      },
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSelectedDatabase(null);
    setSelectedStatus(null);
    setSelectedHost(null);
    setHostInput("");
    resetPage();
  };

  return (
    <PageShell>
      {contextHolder}

      <PageHeader
        eyebrow="Admin · Database · Inventory"
        title="Databases"
        subtitle="Databases discovered by your agents, with connection status and MFA coverage."
        chips={
          <>
            <CountChip count={totalCount} label={totalCount === 1 ? "Database" : "Databases"} />
            {dbNames.length > 0 && (
              <CountChip
                count={dbNames.length}
                label={dbNames.length === 1 ? "Unique name" : "Unique names"}
              />
            )}
          </>
        }
        actions={
          <Link to="/pam/databases/add/linux">
            <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />}>
              Install Database Agent
            </Button>
          </Link>
        }
      />

      <FilterCard
        columns="1.6fr 1fr 1fr"
        applied={appliedFilters}
        onClearAll={clearFilters}
        showing={dbList.length}
        total={totalCount}
      >
        <FilterField label="Host">
          <Input
            allowClear
            placeholder="Search by host (e.g. 52.142)"
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            value={hostInput}
            style={{ height: 36 }}
            onChange={(e) => {
              const value = e.target.value;
              setHostInput(value);
              if (hostSearchTimeout) clearTimeout(hostSearchTimeout);
              setHostSearchTimeout(
                setTimeout(() => {
                  setSelectedHost(value || null);
                  setCurrentPage(0);
                }, 500),
              );
            }}
          />
        </FilterField>

        <FilterField label="Database">
          <Select
            showSearch
            allowClear
            placeholder="Any database"
            style={{ width: "100%", height: 36 }}
            value={selectedDatabase}
            onChange={(value) => {
              setSelectedDatabase(value ?? null);
              setCurrentPage(0);
            }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            // Names arrive as "dbname (host)" — show/filter on the name only.
            options={[...new Set(dbNames.map((db) => db.split(" (")[0]))].map((name) => ({
              label: name,
              value: name,
            }))}
          />
        </FilterField>

        <FilterField label="Status">
          <Select
            allowClear
            placeholder="Any status"
            style={{ width: "100%", height: 36 }}
            value={selectedStatus}
            onChange={(value) => {
              setSelectedStatus(value ?? null);
              setCurrentPage(0);
            }}
            options={[
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={dbList}
          loading={isLoading}
          rowKey={(record) => record.id}
          locale={tableEmpty(
            isLoading,
            appliedFilters.length > 0
              ? "No databases match your filters."
              : "No databases found yet — install a database agent to get started.",
          )}
          pagination={{
            current: currentPage + 1,
            pageSize,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page) => setCurrentPage(page - 1),
          }}
        />
      </TableCard>

      <DatabaseSettingsCard />
    </PageShell>
  );
};

export default DatabasesScreen;
